import React, { createContext, useState, useEffect, useContext } from 'react';
import { authService } from '../services';

// Create the authentication context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Authentication provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is logged in on app load
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const token = localStorage.getItem('sbwom_token');
        if (token) {
          // Verify token with server
          const response = await authService.getProfile();
          if (response) {
            setCurrentUser(response);
          } else {
            // Token is invalid, remove it
            localStorage.removeItem('sbwom_token');
          }
        }
      } catch (err) {
        console.error('Authentication check failed:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  // Login function
  const login = async (credentials) => {
    try {
      setError(null);
      const response = await authService.login(credentials);

      if (response.token) {
        setCurrentUser(response.user);  // Fixed: Extract user object from response
        return { success: true };
      } else {
        setError(response.error || 'Login failed');
        return { success: false, message: response.error || 'Login failed' };
      }
    } catch (err) {
      const errorMessage = err.message || 'Login failed';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      setError(null);
      const response = await authService.register(userData);

      if (response.token) {
        setCurrentUser(response.user);  // Fixed: Extract user object from response
        return { success: true };
      } else {
        setError(response.error || 'Registration failed');
        return { success: false, message: response.error || 'Registration failed' };
      }
    } catch (err) {
      const errorMessage = err.message || 'Registration failed';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    }
  };

  // Google login function
  const googleLogin = async (idToken) => {
    try {
      setError(null);
      const response = await authService.googleLogin(idToken);
      
      if (response.token) {
        setCurrentUser(response.user);  // Fixed: Use response.user instead of response
        return { success: true };
      } else {
        setError(response.error || 'Google login failed');
        return { success: false, message: response.error || 'Google login failed' };
      }
    } catch (err) {
      const errorMessage = err.message || 'Google login failed';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await authService.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setCurrentUser(null);
    }
  };

  // Update profile function
  const updateProfile = async (userData) => {
    try {
      setError(null);
      const response = await authService.updateProfile(userData);
      
      if (response) {
        setCurrentUser(response);
        return { success: true };
      } else {
        setError('Failed to update profile');
        return { success: false, message: 'Failed to update profile' };
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to update profile';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    }
  };

  // Set user directly (for passkey/OAuth flows that handle auth externally)
  const setUser = (userData, token = null) => {
    if (token) {
      localStorage.setItem('sbwom_token', token);
    }
    setCurrentUser(userData);
  };

  const value = {
    currentUser,
    loading,
    error,
    login,
    register,
    googleLogin,
    logout,
    updateProfile,
    setUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;